/* HC - IB Gift Engine (workstream A4) - 2026-07-20
 *
 * First-party replacement for EasyGift's storefront gift behavior.
 * Two layers, deliberately separated:
 *
 *   1. Pure evaluator  — IBGiftEngine.evaluate(cartLines, customerCtx, manifest, now)
 *                        computes desired reward state. No DOM, no network, no clock
 *                        reads of its own. Unit-tested directly.
 *   2. Reconciler      — subscribes to the theme cart event bus and converges the
 *                        cart toward the evaluator's desired state, acting only
 *                        AFTER the shopper's own mutation has settled.
 *
 * Hard invariants (see reports/ib-promotions-plan.md §3 — violating these is a bug):
 *   - Never patch the browser's network primitives (the EasyGift sin).
 *   - Never delay, wrap, or block the shopper's original cart mutation.
 *   - Zero network requests on the cart path: the manifest arrives before the
 *     shopper acts (window.IBGiftManifest from the loader) and all evaluation is
 *     local. The engine's only writes go through theme.cart's queued API.
 *   - At most ONE corrective cart mutation per reconcile pass, idempotent,
 *     converging within a bounded number of passes.
 *   - Reward lines are marked with the `_ib_reward` line property ("ruleId@version").
 *     Other systems' line properties (EasyGift's gift marker, the shoppable-promotions
 *     marker) are never read or written.
 *   - Rewards are only inserted for rules whose manifest entry carries
 *     pricingVerified === true (an active pricing counterpart exists) — otherwise a
 *     "free" gift would be charged at full price.
 *   - Any error fails open: purchased items and the shopper's flow are unaffected.
 */
(function () {
  'use strict';

  var SOURCE = 'ib-gift-engine';
  var REWARD_PROP = '_ib_reward';
  var ENGINE_VERSION = '0.1.0';
  var MAX_PASSES_PER_TRIGGER = 3;
  var CHOICE_STORAGE_KEY = 'ib:gift:choices';
  var OPTOUT_STORAGE_KEY = 'ib:gift:optout';

  /* ------------------------------------------------------------------ *
   * Small utilities
   * ------------------------------------------------------------------ */

  function safeParseJSON(text, fallback) {
    try {
      var parsed = JSON.parse(text);
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (err) {
      return fallback;
    }
  }

  function storageGet(key, fallback) {
    try {
      if (typeof sessionStorage === 'undefined') return fallback;
      var raw = sessionStorage.getItem(key);
      if (raw === null || raw === undefined) return fallback;
      return safeParseJSON(raw, fallback);
    } catch (err) {
      return fallback;
    }
  }

  function storageSet(key, value) {
    try {
      if (typeof sessionStorage === 'undefined') return;
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      /* private browsing / quota — opt-out and choices simply won't persist */
    }
  }

  function debugLog() {
    try {
      if (typeof console !== 'undefined' && console.debug) {
        console.debug.apply(console, ['[IBGiftEngine]'].concat([].slice.call(arguments)));
      }
    } catch (err) { /* noop */ }
  }

  function rewardToken(ruleId, version) {
    return String(ruleId) + '@' + String(version);
  }

  function parseRewardToken(value) {
    if (typeof value !== 'string') return null;
    var at = value.lastIndexOf('@');
    if (at <= 0) return null;
    return { ruleId: value.slice(0, at), version: value.slice(at + 1) };
  }

  /* ------------------------------------------------------------------ *
   * Manifest normalization
   *
   * Accepts the compact qualifier-manifest format (delta-encoded sorted id
   * arrays, `s: "d"`) or plain arrays, and an accompanying `rules` list.
   * ------------------------------------------------------------------ */

  function decodeIdArray(entry) {
    if (!entry || entry.length === 0) return [];
    var out = [entry[0]];
    for (var i = 1; i < entry.length; i++) {
      out.push(out[i - 1] + entry[i]);
    }
    return out;
  }

  function toIdSet(arr, isDelta) {
    var ids = isDelta ? decodeIdArray(arr || []) : (arr || []);
    var set = {};
    for (var i = 0; i < ids.length; i++) set[ids[i]] = true;
    return set;
  }

  function normalizeManifest(raw) {
    if (!raw || typeof raw !== 'object') return null;
    /* In ib-gift-manifest/v1 EVERY bucket array is delta-encoded ([first,
     * then differences]) and `s` is a SOURCE tag ('d' = discount-kit,
     * 'e' = easygift) — it is NOT an encoding flag. Misreading it left
     * every EasyGift bucket as raw deltas once (only the first ID matched);
     * the legacy `s === 'd'` reading survives only for fixture manifests. */
    var v1Schema = typeof raw.schema === 'string' && raw.schema.indexOf('ib-gift-manifest/') === 0;
    var buckets = {};
    var srcBuckets = raw.buckets || {};
    for (var id in srcBuckets) {
      if (!Object.prototype.hasOwnProperty.call(srcBuckets, id)) continue;
      var b = srcBuckets[id] || {};
      var isDelta = v1Schema || b.s === 'd';
      buckets[id] = {
        products: toIdSet(b.p, isDelta),
        variants: toIdSet(b.v, isDelta)
      };
    }

    var rules = [];
    /* Fixture/plain manifests carry engine rules directly; the v1+ schema
     * branch below replaces this with adapted gift entries. */
    var srcRules = raw.gifts && typeof raw.schema === 'string' && raw.schema.indexOf('ib-gift-manifest/') === 0
      ? []
      : (raw.rules || []);

    /* ib-gift-manifest/v1 (the A3 generator's shipped schema): executable gift
     * rules live in raw.gifts keyed by rule id — raw.rules there is DK
     * qualifier-membership metadata, not engine rules. Adapt each gift entry
     * to the engine's rule contract. */
    if (typeof raw.schema === 'string' && raw.schema.indexOf('ib-gift-manifest/') === 0 && raw.gifts) {
      srcRules = [];
      for (var gid in raw.gifts) {
        if (!Object.prototype.hasOwnProperty.call(raw.gifts, gid)) continue;
        var g = raw.gifts[gid] || {};
        var cond = g.cond || {};
        var metric = String(cond.metric || '').toUpperCase();
        /* COMBINATION triggers are not evaluable from the v1 manifest (per-
         * collection membership is merged away) — skip until manifest v2. */
        if (metric === 'COMBINATION') continue;
        if (!g.q || g.q.length !== 1 || !g.reward || !g.reward.options || !g.reward.options.length) continue;
        var isValue = metric === 'SUBTOTAL' || metric === 'VALUE';
        var mode = String(g.mode || '').toUpperCase() === 'CHOICE' ? 'choice' : 'auto';
        var options = [];
        for (var oi = 0; oi < g.reward.options.length; oi++) {
          var opt = g.reward.options[oi] || {};
          var normalized = {
            variantId: opt.v,
            productId: opt.p,
            quantity: mode === 'auto' ? (g.reward.qty || 1) : 1,
            oos: !!opt.oos
          };
          /* v2 display fields (title/thumbnail/handle) pass through for the
           * choice UI; absent on v1 manifests — UI falls back to placeholders. */
          if (opt.t) normalized.title = opt.t;
          if (opt.img) normalized.image = opt.img;
          if (opt.h) normalized.handle = opt.h;
          /* v2.1: retail value in dollars — the UI's "$N VALUE" tag. */
          if (typeof opt.val === 'number' && isFinite(opt.val) && opt.val > 0) normalized.value = opt.val;
          options.push(normalized);
        }
        var sched = g.sched || {};
        srcRules.push({
          id: gid,
          version: raw.meta && raw.meta.generatedAt ? raw.meta.generatedAt : 'v1',
          active: true,
          name: g.name,
          trigger: {
            bucket: g.q[0],
            metric: isValue ? 'value' : 'quantity',
            /* Manifest value thresholds are dollars; the engine compares
             * original_line_price cents. */
            min: isValue && cond.min != null ? Math.round(cond.min * 100) : cond.min,
            max: isValue && cond.max != null ? Math.round(cond.max * 100) : cond.max,
            /* EasyGift semantics: the qualifying count excludes gift lines. */
            countRewardUnits: false
          },
          /* per must live in the same unit as the evaluated amount: cents for
           * value rules (review F1: a dollars-denominated per made a $530 cart
           * compute floor(53025/497.5) = 106 reward units). */
          recurrence: {
            per: isValue && cond.min != null ? Math.round(cond.min * 100) : cond.min,
            cap: (g.rec && g.rec.cap) || 1
          },
          reward: { mode: mode, options: options, sameVariant: !!g.reward.sameVariant },
          window: (sched.starts || sched.ends)
            ? { start: sched.starts || null, end: sched.ends || null }
            : null,
          /* pp 'priced' = verified DK pricing; 'zero-price' = $0 catalog
           * collateral. 'none' must never be inserted (unpriced reward). */
          pricingVerified: g.pp === 'priced' || g.pp === 'zero-price'
        });
      }
    }

    for (var i = 0; i < srcRules.length; i++) {
      var r = srcRules[i];
      if (!r || !r.id || !r.trigger || !r.reward) continue;
      if (r.active === false) continue;
      if (!buckets[r.trigger.bucket]) continue;
      if (!r.reward.options || !r.reward.options.length) continue;
      rules.push(r);
    }

    return {
      version: raw.version || (raw.meta && raw.meta.generatedAt) || 'unknown',
      buckets: buckets,
      rules: rules
    };
  }

  /* ------------------------------------------------------------------ *
   * Pure evaluator
   *
   * cartLines: /cart.js style items (variant_id, product_id, quantity,
   *            original_line_price in cents, properties).
   * customerCtx: { choices: { ruleId: [variantId, ...] }, now: msEpoch }
   * Returns { targets, choices, reasons } — never throws.
   * ------------------------------------------------------------------ */

  function lineIsEngineReward(line) {
    return !!(line && line.properties && line.properties[REWARD_PROP]);
  }

  /* HC - legacy-cart takeover (A8) - 2026-07-22
   * `_Gifted` is EasyGift's addedItemIdentifier (dependency audit: EG is its
   * only writer AND only reader; we never write it). After cutover, live carts
   * still carry EG-added gift lines. The engine ADOPTS them virtually: they
   * count toward fulfillment and are excluded from qualifier math exactly like
   * engine rewards, so the happy-path cutover pass mutates NOTHING. Excess or
   * dequalified legacy gifts converge like our own (trim/remove — matching
   * EG's own expired-gift cleanup). `_Gifted` lines whose variant matches no
   * known rule's reward option stay foreign-untouched, and Discount Kit's own
   * gift lines are that app's domain — never considered here. */
  var LEGACY_GIFT_PROP = '_Gifted';

  function lineIsLegacyGift(line) {
    return !!(line && line.properties &&
      Object.prototype.hasOwnProperty.call(line.properties, LEGACY_GIFT_PROP) &&
      !line.properties[REWARD_PROP]);
  }

  function ruleWindowOpen(rule, now) {
    var w = rule.window;
    if (!w) return true;
    if (w.start && now < w.start) return false;
    if (w.end && now >= w.end) return false;
    return true;
  }

  function qualifyingAmount(rule, bucket, cartLines) {
    var metric = rule.trigger.metric === 'value' ? 'value' : 'quantity';
    var total = 0;
    for (var i = 0; i < cartLines.length; i++) {
      var line = cartLines[i];
      if (!line) continue;
      /* Legacy EG gifts are excluded from qualifier math exactly like our own
       * rewards (EG itself excluded `_Gifted` lines from trigger calculation,
       * so this preserves pre-cutover counting semantics). */
      if ((lineIsEngineReward(line) || lineIsLegacyGift(line)) && !rule.trigger.countRewardUnits) continue;
      var inBucket = bucket.variants[line.variant_id] || bucket.products[line.product_id];
      if (!inBucket) continue;
      if (metric === 'value') {
        total += typeof line.original_line_price === 'number'
          ? line.original_line_price
          : (line.line_price || 0);
      } else {
        total += line.quantity || 0;
      }
    }
    return total;
  }

  function recurrenceCount(rule, amount) {
    var min = rule.trigger.min || 0;
    if (amount < min) return 0;
    if (rule.trigger.max !== undefined && rule.trigger.max !== null && amount > rule.trigger.max) return 0;
    var rec = rule.recurrence;
    if (!rec || !rec.per) return 1;
    var k = Math.floor(amount / rec.per);
    if (k < 1) k = amount >= min ? 1 : 0;
    if (rec.cap) k = Math.min(k, rec.cap);
    return k;
  }

  function evaluate(cartLines, customerCtx, manifest, now) {
    var result = { targets: [], choices: [], reasons: [] };
    try {
      if (!manifest || !manifest.rules) return result;
      cartLines = cartLines || [];
      customerCtx = customerCtx || {};
      var choiceState = customerCtx.choices || {};
      var deadVariants = customerCtx.unavailableVariants || {};
      var effOptions = function (rule) {
        var out = [];
        for (var eo = 0; eo < rule.reward.options.length; eo++) {
          var op = rule.reward.options[eo];
          out.push(deadVariants[String(op.variantId)] && !op.oos
            ? { variantId: op.variantId, productId: op.productId, quantity: op.quantity,
                oos: true, title: op.title, image: op.image, handle: op.handle, value: op.value }
            : op);
        }
        return out;
      };
      now = typeof now === 'number' ? now : 0;

      for (var i = 0; i < manifest.rules.length; i++) {
        var rule = manifest.rules[i];
        var reason = function (code) {
          result.reasons.push({ ruleId: rule.id, code: code });
        };

        if (rule.pricingVerified !== true) { reason('pricing_unverified'); continue; }
        if (!ruleWindowOpen(rule, now)) { reason('not_in_window'); continue; }

        var bucket = manifest.buckets[rule.trigger.bucket];
        var amount = qualifyingAmount(rule, bucket, cartLines);
        var k = recurrenceCount(rule, amount);
        if (k === 0) {
          reason(amount > 0 && rule.trigger.max !== undefined && rule.trigger.max !== null && amount > rule.trigger.max
            ? 'above_maximum'
            : 'below_minimum');
          continue;
        }

        var options = effOptions(rule);
        if (rule.reward.mode === 'choice') {
          /* HC - single-option promotion (decisions §8 addendum) - 2026-07-20
           * A "choice" with exactly one in-stock option is not a real choice:
           * auto-add it (the UI shows a toast instead of a picker). A sole
           * option that is out of stock is a quiet no-op for the rule. */
          var inStock = [];
          for (var io = 0; io < options.length; io++) {
            if (!options[io].oos) inStock.push(options[io]);
          }
          if (inStock.length === 1) {
            pushTarget(result.targets, rule, inStock[0], k);
            reason('auto_single_option');
            continue;
          }
          if (inStock.length === 0) {
            /* Review F9: every option sold out = quiet no-op regardless of
             * option count — never a picker full of dead tiles. */
            reason('reward_out_of_stock');
            continue;
          }
          var selected = [];
          var chosen = choiceState[rule.id] || [];
          /* Review F7: stored picks fill slots and become targets ONLY while
           * their option is in stock — a sold-out pick neither inserts nor
           * blocks the slot (the picker resurfaces it as pending). */
          for (var c = 0; c < chosen.length && selected.length < k; c++) {
            for (var o = 0; o < inStock.length; o++) {
              if (String(inStock[o].variantId) === String(chosen[c])) {
                selected.push(inStock[o]);
                break;
              }
            }
          }
          result.choices.push({
            ruleId: rule.id,
            version: rule.version,
            slots: k,
            options: options,
            selected: selected
          });
          for (var s = 0; s < selected.length; s++) {
            pushTarget(result.targets, rule, selected[s], 1);
          }
          reason(selected.length < k ? 'choice_required' : 'qualified');
        } else {
          /* AUTO rules: never target a sold-out or session-dead reward — a
           * dead reward would otherwise retry-loop every pass. */
          var pushed = false;
          for (var a = 0; a < options.length; a++) {
            if (options[a].oos) continue;
            pushTarget(result.targets, rule, options[a], k);
            pushed = true;
          }
          reason(pushed ? 'qualified' : 'reward_out_of_stock');
        }
      }
    } catch (err) {
      debugLog('evaluate failed open', err);
      return { targets: [], choices: [], reasons: [{ ruleId: null, code: 'evaluator_error' }] };
    }
    return result;
  }

  function pushTarget(targets, rule, option, multiplier) {
    var qty = (option.quantity || 1) * multiplier;
    for (var i = 0; i < targets.length; i++) {
      if (targets[i].ruleId === rule.id && String(targets[i].variantId) === String(option.variantId)) {
        targets[i].quantity += qty;
        return;
      }
    }
    targets.push({
      ruleId: rule.id,
      version: rule.version,
      variantId: option.variantId,
      quantity: qty
    });
  }

  /* ------------------------------------------------------------------ *
   * Reconciler
   * ------------------------------------------------------------------ */

  var state = {
    initialized: false,
    active: false,
    manifest: null,
    busy: false,
    passCount: 0,
    unavailableVariants: {},
    newerCartWhileBusy: false,
    lastOwnedQty: null,
    lastCart: null
  };

  function themeCart() {
    return (typeof theme !== 'undefined' && theme && theme.cart) ||
      (typeof window !== 'undefined' && window.theme && window.theme.cart) || null;
  }

  function ownedRewardLines(cart, manifest) {
    var owned = [];
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!lineIsEngineReward(line)) continue;
      var token = parseRewardToken(line.properties[REWARD_PROP]);
      if (!token) continue;
      /* Engine-owned = the token's rule exists AND the line's variant is one
       * of that rule's reward options (review F15: a known ruleId on an
       * unrelated variant is foreign — never touched). Version is deliberately
       * lenient: every manifest regen changes it, and legacy gift lines must
       * stay managed across regens, not orphaned. */
      if (manifest && manifest.rules) {
        var rule = null;
        for (var r = 0; r < manifest.rules.length; r++) {
          if (manifest.rules[r].id === token.ruleId) { rule = manifest.rules[r]; break; }
        }
        /* Review R2-13: OUR token on a rule that no longer exists is engine
         * debris from a removed promotion — cleaned up (as `stale`), never
         * abandoned as a possibly-charged "gift". A KNOWN rule with a variant
         * outside its options stays foreign-untouched (F15): that shape is a
         * forgery, not our history. */
        if (!rule) {
          owned.push({ line: line, ruleId: token.ruleId, version: token.version, stale: true });
          continue;
        }
        var variantOk = false;
        for (var o = 0; o < rule.reward.options.length; o++) {
          if (String(rule.reward.options[o].variantId) === String(line.variant_id)) { variantOk = true; break; }
        }
        if (!variantOk) continue;
      }
      owned.push({ line: line, ruleId: token.ruleId, version: token.version });
    }
    return owned;
  }

  /* Legacy EG gift lines attributed to the rule whose reward options contain
   * their variant. When a variant appears in several rules' options, prefer a
   * rule the current evaluation says qualifies (ruleQualifies is optional —
   * baseline callers pass null and get first-known attribution). Lines whose
   * variant matches no known rule are NOT returned: they are foreign debris
   * from retired promotions and stay untouched (fail-open). */
  function legacyRewardLines(cart, manifest, ruleQualifies) {
    var legacy = [];
    var items = (cart && cart.items) || [];
    if (!manifest || !manifest.rules) return legacy;
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (!lineIsLegacyGift(line)) continue;
      var chosen = null;
      for (var r = 0; r < manifest.rules.length; r++) {
        var rule = manifest.rules[r];
        for (var o = 0; o < rule.reward.options.length; o++) {
          if (String(rule.reward.options[o].variantId) === String(line.variant_id)) {
            if (!chosen) chosen = rule.id;
            if (ruleQualifies && ruleQualifies[rule.id]) { chosen = rule.id; r = manifest.rules.length; }
            break;
          }
        }
      }
      if (chosen) legacy.push({ line: line, ruleId: chosen });
    }
    return legacy;
  }

  function customerCtx() {
    return {
      choices: storageGet(CHOICE_STORAGE_KEY, {}),
      /* Variants whose add failed non-transiently this session: the evaluator
       * treats them as sold out, so a dead CHOICE option resurfaces the picker
       * with the alternatives instead of blocklisting the whole rule, and an
       * AUTO rule with a dead reward stops retry-looping. */
      unavailableVariants: state.unavailableVariants
    };
  }

  /* A stored pick lives exactly as long as the slot it fills (Noah 2026-07-21:
   * re-earning a choice offer must re-open the picker, never silently re-add
   * the previous pick). Rules that dequalify lose all stored picks; a shrunken
   * slot count drops the excess. Never prunes on an evaluator error. */
  function pruneChoices(desired) {
    for (var r = 0; r < desired.reasons.length; r++) {
      if (desired.reasons[r].code === 'evaluator_error') return;
    }
    var choices = storageGet(CHOICE_STORAGE_KEY, {});
    var byRule = {};
    for (var c = 0; c < desired.choices.length; c++) {
      var ch = desired.choices[c];
      var inStockSet = {};
      for (var o = 0; o < (ch.options || []).length; o++) {
        if (!ch.options[o].oos) inStockSet[String(ch.options[o].variantId)] = true;
      }
      byRule[ch.ruleId] = { slots: ch.slots, inStock: inStockSet };
    }
    var changed = false;
    for (var ruleId in choices) {
      if (!Object.prototype.hasOwnProperty.call(choices, ruleId)) continue;
      var entry = byRule[ruleId];
      if (!entry || entry.slots === 0) {
        delete choices[ruleId];
        changed = true;
        continue;
      }
      /* Review F8: drop picks whose option is sold out or gone BEFORE slicing,
       * so a replacement pick appended after an OOS one survives the trim. */
      var kept = [];
      for (var p = 0; p < choices[ruleId].length; p++) {
        if (entry.inStock[String(choices[ruleId][p])]) kept.push(choices[ruleId][p]);
      }
      if (kept.length > entry.slots) kept = kept.slice(0, entry.slots);
      if (kept.length !== choices[ruleId].length) {
        choices[ruleId] = kept;
        changed = true;
      }
    }
    if (changed) storageSet(CHOICE_STORAGE_KEY, choices);
  }

  /* A legacy EG gift that fills a CHOICE rule's option was picked by the
   * shopper pre-cutover (via EG's own picker). Seed the session pick state so
   * the modal presents that slot as already filled instead of re-asking.
   * Count-aware (a qty-2 line fills two slots), capped at the rule's slots,
   * in-stock options only (an OOS pick would be pruned immediately anyway).
   * Idempotent: appends only what stored picks don't already cover. */
  function seedLegacyChoicePicks(cart, desired, manifest) {
    if (!desired || !desired.choices || !desired.choices.length) return false;
    var chQualifies = {};
    for (var q = 0; q < desired.choices.length; q++) chQualifies[desired.choices[q].ruleId] = true;
    var legacy = legacyRewardLines(cart, manifest, chQualifies);
    if (!legacy.length) return false;
    var choices = storageGet(CHOICE_STORAGE_KEY, {});
    var changed = false;
    for (var c = 0; c < desired.choices.length; c++) {
      var ch = desired.choices[c];
      var optionOk = {};
      for (var o = 0; o < (ch.options || []).length; o++) {
        if (!ch.options[o].oos) optionOk[String(ch.options[o].variantId)] = true;
      }
      var legacyCount = {};
      for (var l = 0; l < legacy.length; l++) {
        if (legacy[l].ruleId !== ch.ruleId) continue;
        var v = String(legacy[l].line.variant_id);
        if (!optionOk[v]) continue;
        legacyCount[v] = (legacyCount[v] || 0) + (legacy[l].line.quantity || 0);
      }
      var stored = choices[ch.ruleId] || [];
      var storedCount = {};
      for (var s = 0; s < stored.length; s++) {
        var sv = String(stored[s]);
        storedCount[sv] = (storedCount[sv] || 0) + 1;
      }
      var appended = stored.slice();
      for (var lv in legacyCount) {
        if (!Object.prototype.hasOwnProperty.call(legacyCount, lv)) continue;
        var deficit = legacyCount[lv] - (storedCount[lv] || 0);
        while (deficit > 0 && appended.length < ch.slots) {
          appended.push(lv);
          deficit--;
        }
      }
      if (appended.length !== stored.length) {
        choices[ch.ruleId] = appended;
        changed = true;
      }
    }
    if (changed) storageSet(CHOICE_STORAGE_KEY, choices);
    return changed;
  }

  function ownedQtyByKey(cart) {
    var owned = ownedRewardLines(cart, state.manifest);
    var map = {};
    for (var i = 0; i < owned.length; i++) {
      var key = owned[i].ruleId + '|' + owned[i].line.variant_id;
      map[key] = (map[key] || 0) + (owned[i].line.quantity || 0);
    }
    /* Adopted legacy gifts join the ownership baseline so a shopper deleting
     * one registers as an opt-out (otherwise the engine would re-add it). */
    var legacy = legacyRewardLines(cart, state.manifest, null);
    for (var l = 0; l < legacy.length; l++) {
      var lk = legacy[l].ruleId + '|' + legacy[l].line.variant_id;
      map[lk] = (map[lk] || 0) + (legacy[l].line.quantity || 0);
    }
    return map;
  }

  /* Every verified authoritative cart advances the ownership baseline — even
   * engine-sourced passes (review F4: skipping them meant a just-added gift was
   * never "previously owned", so an immediate shopper removal read as merely
   * missing and was re-added). */
  function updateOwnershipBaseline(cart) {
    state.lastOwnedQty = ownedQtyByKey(cart);
  }

  function detectOptOuts(cart, desired) {
    /* A shopper-initiated event where owned reward quantity DROPPED — fully or
     * partially (review F5) — while the engine still wants it = the shopper
     * removed or reduced the gift. Respect it for the rest of the session
     * (until qualification resets to zero). */
    var current = ownedQtyByKey(cart);

    var optout = storageGet(OPTOUT_STORAGE_KEY, {});
    var changed = false;

    if (state.lastOwnedQty) {
      var desiredQty = {};
      for (var t = 0; t < desired.targets.length; t++) {
        var dk = desired.targets[t].ruleId + '|' + desired.targets[t].variantId;
        desiredQty[dk] = desired.targets[t].quantity;
      }
      for (var key in state.lastOwnedQty) {
        if (!Object.prototype.hasOwnProperty.call(state.lastOwnedQty, key)) continue;
        var cur = current[key] || 0;
        if (cur >= state.lastOwnedQty[key]) continue;   /* no reduction */
        if ((desiredQty[key] || 0) > cur) {             /* we would top it back up */
          optout[key.slice(0, key.indexOf('|'))] = true;
          changed = true;
        }
      }
    }

    /* Reset opt-outs for rules that no longer qualify at all. */
    var qualifies = {};
    for (var q = 0; q < desired.targets.length; q++) qualifies[desired.targets[q].ruleId] = true;
    for (var cq = 0; cq < desired.choices.length; cq++) qualifies[desired.choices[cq].ruleId] = true;
    for (var existing in optout) {
      if (Object.prototype.hasOwnProperty.call(optout, existing) && !qualifies[existing]) {
        delete optout[existing];
        changed = true;
      }
    }

    if (changed) storageSet(OPTOUT_STORAGE_KEY, optout);
    state.lastOwnedQty = current;
    return optout;
  }

  function computeDiff(cart, desired, optout, manifest) {
    /* ownedRewardLines validates rule + reward-variant, so everything here is
     * genuinely engine-owned; forged/stale tokens never reach this list. */
    var owned = ownedRewardLines(cart, manifest);

    /* Which rules still qualify this pass — from the evaluator's own reasons.
     * reward_out_of_stock means qualified-but-unfulfillable; window/threshold/
     * pricing codes mean NOT qualified. */
    var ruleQualifies = {};
    for (var rq = 0; rq < (desired.reasons || []).length; rq++) {
      var reasonEntry = desired.reasons[rq];
      if (!reasonEntry || !reasonEntry.ruleId) continue;
      if (reasonEntry.code === 'qualified' || reasonEntry.code === 'auto_single_option' ||
          reasonEntry.code === 'choice_required' || reasonEntry.code === 'reward_out_of_stock') {
        ruleQualifies[reasonEntry.ruleId] = true;
      }
    }

    /* Removed-rule debris goes first: one update pass zeroes those lines
     * (review R2-13). They never enter the quantity model. */
    var live = [];
    var staleUpdates = {};
    var hasStale = false;
    for (var s = 0; s < owned.length; s++) {
      if (owned[s].stale) {
        staleUpdates[owned[s].line.key] = 0;
        hasStale = true;
      } else {
        live.push(owned[s]);
      }
    }
    owned = live;

    /* Adopted legacy EG gifts (A8 takeover) count toward fulfillment alongside
     * owned lines, so a cutover pass over an already-correct cart is a no-op
     * and never double-adds. They share the entitlement ceiling: excess (e.g.
     * a stale tab where EG's patch re-added) trims, dequalified known gifts
     * are removed — identical semantics to our own lines. */
    var legacy = legacyRewardLines(cart, manifest, ruleQualifies);

    /* Current gift quantity per rule+variant (owned + adopted legacy). */
    var currentQty = {};
    for (var i = 0; i < owned.length; i++) {
      var o = owned[i];
      var ckey = o.ruleId + '|' + o.line.variant_id;
      currentQty[ckey] = (currentQty[ckey] || 0) + (o.line.quantity || 0);
    }
    for (var li = 0; li < legacy.length; li++) {
      var lkey = legacy[li].ruleId + '|' + legacy[li].line.variant_id;
      currentQty[lkey] = (currentQty[lkey] || 0) + (legacy[li].line.quantity || 0);
    }

    /* Desired quantity per rule+variant — UNSUPPRESSED (reviews R2-6/R2-7:
     * opt-outs and dead variants gate ADDITIONS only; folding them in here
     * once turned the shopper's kept gift, or a valid line whose top-up
     * failed, into "excess" and deleted it). */
    var desiredQty = {};
    for (var d = 0; d < desired.targets.length; d++) {
      var target = desired.targets[d];
      desiredQty[target.ruleId + '|' + target.variantId] = {
        quantity: target.quantity,
        target: target
      };
    }

    /* Removals / down-adjustments: distribute the FULL excess across every
     * matching line in one update call (review F16: correcting only the first
     * duplicate line per pass could exhaust the pass budget with excess still
     * in the cart). */
    var updates = staleUpdates;
    var hasUpdates = hasStale;
    var excess = {};
    for (var ek in currentQty) {
      if (!Object.prototype.hasOwnProperty.call(currentQty, ek)) continue;
      /* The entitlement ceiling stands for every owned line (review R2-6: an
       * opt-out must not disable it — a later inflation above the rule's
       * entitlement still trims back). What opt-out and dead-variant status
       * suppress is ADDING, below. F5 stays honored because want reflects the
       * full entitlement: a shopper's reduction sits BELOW it, never above.
       * One carve-out (review R2-7): a session-dead variant vanishes from the
       * evaluator's targets entirely, so its want reads 0 — while its rule
       * still QUALIFIES (the evaluator says reward_out_of_stock, not
       * below_minimum), the shopper's existing unit is valid and kept. */
      var ekVariant = ek.slice(ek.indexOf('|') + 1);
      var ekRule = ek.slice(0, ek.indexOf('|'));
      if (state.unavailableVariants[ekVariant] && !desiredQty[ek] && ruleQualifies[ekRule]) continue;
      var wantQty = desiredQty[ek] ? desiredQty[ek].quantity : 0;
      if (currentQty[ek] > wantQty) excess[ek] = currentQty[ek] - wantQty;
    }
    /* Legacy lines absorb cuts FIRST: converging debris before marked lines
     * leaves surviving gifts engine-owned, so later passes get cleaner. */
    var cuttable = legacy.concat(owned);
    for (var r = 0; r < cuttable.length; r++) {
      var ow = cuttable[r];
      var dkey = ow.ruleId + '|' + ow.line.variant_id;
      if (!excess[dkey]) continue;
      var cut = Math.min(ow.line.quantity || 0, excess[dkey]);
      if (cut <= 0) continue;
      updates[ow.line.key] = (ow.line.quantity || 0) - cut;
      excess[dkey] -= cut;
      hasUpdates = true;
    }
    for (var xk in excess) {
      if (Object.prototype.hasOwnProperty.call(excess, xk) && excess[xk] > 0) {
        currentQty[xk] = (desiredQty[xk] ? desiredQty[xk].quantity : 0);
      }
    }

    /* Additions (one add call, possibly multi-item). Opt-outs and dead
     * variants suppress HERE — and only here. */
    var adds = [];
    for (var akey in desiredQty) {
      if (!Object.prototype.hasOwnProperty.call(desiredQty, akey)) continue;
      var wantEntry = desiredQty[akey];
      if (optout[wantEntry.target.ruleId]) continue;
      if (state.unavailableVariants[String(wantEntry.target.variantId)]) continue;
      var haveQty = currentQty[akey] || 0;
      if (wantEntry.quantity > haveQty) {
        var props = {};
        props[REWARD_PROP] = rewardToken(wantEntry.target.ruleId, wantEntry.target.version);
        adds.push({
          id: wantEntry.target.variantId,
          quantity: wantEntry.quantity - haveQty,
          properties: props,
          _ruleId: wantEntry.target.ruleId
        });
      }
    }

    return { updates: hasUpdates ? updates : null, adds: adds };
  }

  /* The UI's commit contract (review F2): after every reconciler mutation
   * settles, broadcast what actually happened so "Added to your order" can be
   * truthful. addedVariantIds = variants whose add call succeeded this pass. */
  function dispatchMutationResult(ok, addedVariantIds, failedRules) {
    try {
      document.dispatchEvent(new CustomEvent('ibgift:mutation-result', {
        detail: { ok: ok, addedVariantIds: addedVariantIds, failedRules: failedRules }
      }));
    } catch (err) { /* noop */ }
  }

  /* A challenge or network hiccup is transient: do NOT blocklist the rule for
   * the session — the next shopper event retries. Only concrete rejections
   * (OOS/422 product errors) mark a rule unavailable (review F6). */
  function isTransientAddError(err) {
    try {
      var cartApi = themeCart();
      if (cartApi && cartApi.isChallengeActive && cartApi.isChallengeActive()) return true;
    } catch (e) { /* noop */ }
    /* Review R2-9: throttles and server errors are conditions of the moment,
     * never proof a product is unavailable. */
    var status = err && (err.status || err.statusCode);
    if (typeof status === 'number' && (status === 429 || status >= 500)) return true;
    var msg = String((err && err.message) || err || '');
    return /network|failed to fetch|load failed|timed? ?out|429|too many requests|5\d\d|service unavailable|bad gateway/i.test(msg);
  }

  /* Review R2-1: post-failure convergence starts from an authoritative cart
   * read (theme-queued, deduped), never a stale snapshot — an ambiguous
   * failure may have landed server-side and a snapshot replay duplicates it.
   * Falls back to the in-flight partial result, else ends the pass. */
  function refreshedCartResult(fallbackResult) {
    try {
      var cartApi = themeCart();
      if (cartApi && typeof cartApi.getCart === 'function') {
        return cartApi.getCart().then(function (cart) {
          var cartState = cartApi.getCartState ? cartApi.getCartState(cart) : cart;
          if (cartState && Array.isArray(cartState.items)) return { cartState: cartState };
          return fallbackResult || null;
        }, function () { return fallbackResult || null; });
      }
    } catch (err) { /* fall through */ }
    return fallbackResult || null;
  }

  /* Batch add with per-item isolation (review F6): a batch rejection retries
   * each item individually so one sold-out reward cannot suppress the others,
   * and only the failing rule is blocklisted. */
  function addRewards(adds) {
    var cartApi = themeCart();
    var items = [];
    for (var i = 0; i < adds.length; i++) {
      items.push({
        id: adds[i].id,
        quantity: adds[i].quantity,
        properties: adds[i].properties,
        _ruleId: adds[i]._ruleId
      });
    }
    var send = function (item) {
      return cartApi.addSilently([{ id: item.id, quantity: item.quantity, properties: item.properties }], { source: SOURCE });
    };
    var batchPayload = [];
    for (var b = 0; b < items.length; b++) {
      batchPayload.push({ id: items[b].id, quantity: items[b].quantity, properties: items[b].properties });
    }
    return cartApi.addSilently(batchPayload, { source: SOURCE }).then(function (result) {
      var addedIds = [];
      for (var a = 0; a < items.length; a++) addedIds.push(String(items[a].id));
      dispatchMutationResult(true, addedIds, []);
      return result;
    }, function (batchErr) {
      if (items.length === 1) {
        var only = items[0];
        if (!isTransientAddError(batchErr)) state.unavailableVariants[String(only.id)] = true;
        dispatchMutationResult(false, [], [{ ruleId: only._ruleId, variantId: String(only.id), reason: String((batchErr && batchErr.message) || 'add_failed') }]);
        debugLog('reward add failed open', batchErr && batchErr.message);
        /* Review R2-1: NEVER converge from a stale snapshot after a failure —
         * an ambiguous network error may have landed the add server-side, and
         * replaying the add from a stale snapshot duplicates it. Refresh the
         * authoritative cart first; if that's unavailable, end the pass and
         * let the next shopper event resync. */
        return refreshedCartResult();
      }
      var added = [];
      var failed = [];
      var chain = Promise.resolve(null);
      items.forEach(function (item) {
        chain = chain.then(function (prev) {
          return send(item).then(function (result) {
            added.push(String(item.id));
            return result || prev;
          }, function (err) {
            if (!isTransientAddError(err)) state.unavailableVariants[String(item.id)] = true;
            failed.push({ ruleId: item._ruleId, variantId: String(item.id), reason: String((err && err.message) || 'add_failed') });
            return prev;
          });
        });
      });
      return chain.then(function (last) {
        dispatchMutationResult(failed.length === 0, added, failed);
        if (failed.length) return refreshedCartResult(last);
        return last;
      });
    });
  }

  function dispatchState(desired) {
    try {
      document.dispatchEvent(new CustomEvent('ibgift:state', {
        detail: {
          engineVersion: ENGINE_VERSION,
          manifestVersion: state.manifest ? state.manifest.version : null,
          targets: desired.targets,
          choices: desired.choices,
          reasons: desired.reasons
        }
      }));
    } catch (err) { /* fail open */ }
  }

  function reconcile(cart, meta) {
    meta = meta || {};
    var cartApi = themeCart();
    if (!state.active || !cartApi || !cart || !Array.isArray(cart.items)) return null;
    if (state.busy) return null;
    if (cartApi.isChallengeActive && cartApi.isChallengeActive()) return null;

    var desired;
    try {
      desired = evaluate(cart.items, customerCtx(), state.manifest, Date.now());
      /* Legacy-cart takeover (A8): adopted EG choice gifts seed the session
       * picks; a seed changes the evaluation (slots fill, their targets
       * appear), so re-run once against the adopted state. */
      if (seedLegacyChoicePicks(cart, desired, state.manifest)) {
        desired = evaluate(cart.items, customerCtx(), state.manifest, Date.now());
      }
      pruneChoices(desired);
      var optout;
      if (meta.fromEngine) {
        /* Engine-sourced verification: no opt-out detection (our own mutation
         * is not shopper intent) but the ownership baseline MUST advance so a
         * following shopper removal is seen as a transition (review F4). */
        updateOwnershipBaseline(cart);
        optout = storageGet(OPTOUT_STORAGE_KEY, {});
      } else {
        optout = detectOptOuts(cart, desired);
      }
      var diff = computeDiff(cart, desired, optout, state.manifest);
      dispatchState(desired);

      if (!diff.updates && diff.adds.length === 0) {
        state.passCount = 0;
        return null;
      }

      if (state.passCount >= MAX_PASSES_PER_TRIGGER) {
        debugLog('pass budget exhausted; deferring to next shopper event');
        state.passCount = 0;
        return null;
      }
      state.passCount += 1;
      state.busy = true;

      var mutation;
      if (diff.updates) {
        /* Stale rewards go first; additions happen on the follow-up pass. */
        mutation = cartApi.updateCartAndBroadcast(diff.updates, { source: SOURCE })
          .then(function (result) {
            dispatchMutationResult(true, [], []);
            return result;
          }, function (err) {
            dispatchMutationResult(false, [], []);
            debugLog('reward update failed open', err && err.message);
            return null;
          });
      } else {
        mutation = addRewards(diff.adds);
      }

      return mutation.then(function (result) {
        state.busy = false;
        /* Review R2-5: a shopper event that arrived mid-mutation is newer
         * truth than this mutation's snapshot — drain it instead. */
        if (state.newerCartWhileBusy) {
          state.newerCartWhileBusy = false;
          state.passCount = 0;
          return reconcile(state.lastCart, { fromEngine: false });
        }
        var cartState = result && cartApi.getCartState ? cartApi.getCartState(result) : null;
        if (cartState && Array.isArray(cartState.items)) {
          state.lastCart = cartState;
          return reconcile(cartState, { fromEngine: true });
        }
        return null;
      }, function (err) {
        state.busy = false;
        debugLog('reconcile mutation failed open', err && err.message);
        if (state.newerCartWhileBusy) {
          state.newerCartWhileBusy = false;
          state.passCount = 0;
          return reconcile(state.lastCart, { fromEngine: false });
        }
        return null;
      });
    } catch (err) {
      state.busy = false;
      debugLog('reconcile failed open', err);
      return null;
    }
  }

  function onCartUpdated(event) {
    try {
      var detail = (event && event.detail) || {};
      if (detail.source === SOURCE) return;
      if (detail.optimistic || detail.countOnly || detail.lightweight) return;
      var cart = detail.cart || detail.cartState;
      if (!cart || !Array.isArray(cart.items)) return;
      state.lastCart = cart;
      state.passCount = 0;
      /* Review R2-5: while a mutation is in flight this pass no-ops on the
       * busy guard — flag it so the continuation drains the NEWER cart
       * instead of clobbering it with its own older result. */
      if (state.busy) state.newerCartWhileBusy = true;
      reconcile(cart, { fromEngine: false });
    } catch (err) {
      debugLog('cart:updated handler failed open', err);
    }
  }

  /* ------------------------------------------------------------------ *
   * Choice API (consumed by the A5 choice UI)
   * ------------------------------------------------------------------ */

  function storeChoices(ruleId, variantIds) {
    var choices = storageGet(CHOICE_STORAGE_KEY, {});
    var list = choices[ruleId] || [];
    /* One entry per earned slot, duplicates allowed — two slots may hold the
     * same variant. Excess entries are pruned against live slot counts. */
    for (var i = 0; i < variantIds.length; i++) list.push(String(variantIds[i]));
    choices[ruleId] = list;
    storageSet(CHOICE_STORAGE_KEY, choices);
    document.dispatchEvent(new CustomEvent('ibgift:choices-updated', { detail: { ruleId: ruleId } }));
  }

  function selectChoice(ruleId, variantId) {
    try {
      storeChoices(ruleId, [variantId]);
      if (state.lastCart) reconcile(state.lastCart, { fromEngine: false });
    } catch (err) {
      debugLog('selectChoice failed open', err);
    }
  }

  /* Commit a whole choice at once: all picks stored, then a SINGLE reconcile —
   * so a multi-pick commit produces one add batch and therefore one
   * ibgift:mutation-result carrying the full ack set (the UI's commit machine
   * confirms 'added' only when every committed variant is acknowledged). */
  function selectChoices(ruleId, variantIds) {
    try {
      var ids = [];
      for (var i = 0; i < (variantIds || []).length; i++) {
        if (variantIds[i] != null) ids.push(variantIds[i]);
      }
      if (!ids.length) return;
      storeChoices(ruleId, ids);
      if (state.lastCart) reconcile(state.lastCart, { fromEngine: false });
    } catch (err) {
      debugLog('selectChoices failed open', err);
    }
  }

  function clearChoices(ruleId) {
    try {
      var choices = storageGet(CHOICE_STORAGE_KEY, {});
      delete choices[ruleId];
      storageSet(CHOICE_STORAGE_KEY, choices);
      document.dispatchEvent(new CustomEvent('ibgift:choices-updated', { detail: { ruleId: ruleId } }));
      if (state.lastCart) reconcile(state.lastCart, { fromEngine: false });
    } catch (err) {
      debugLog('clearChoices failed open', err);
    }
  }

  /* ------------------------------------------------------------------ *
   * Bootstrap
   * ------------------------------------------------------------------ */

  function init(config) {
    config = config || {};
    if (state.initialized) return api;
    state.initialized = true;

    var manifestSource = config.manifest ||
      (typeof window !== 'undefined' ? window.IBGiftManifest : null);

    /* A3 loader interface: window.IBGiftManifest is an {init, get} object, not
     * a promise. init(url) starts (or reuses) the single page fetch and returns
     * a promise that resolves the manifest or null — never rejects. */
    if (manifestSource && typeof manifestSource.then !== 'function' &&
        typeof manifestSource.init === 'function') {
      var manifestUrl = config.manifestUrl ||
        (typeof window !== 'undefined' ? window.__IB_GIFT_MANIFEST_URL__ : null);
      manifestSource = manifestSource.init(manifestUrl);
    }

    /* Review F3: subscribe BEFORE the manifest resolves. onCartUpdated
     * buffers the latest authoritative cart into state.lastCart even while
     * inactive (reconcile no-ops until activation), so qualifying activity
     * during manifest load is never lost. */
    try {
      document.addEventListener('cart:updated', onCartUpdated);
    } catch (err) {
      debugLog('cart listener attach failed; engine dormant', err && err.message);
      return api;
    }

    var activate = function (rawManifest) {
      var normalized = normalizeManifest(rawManifest);
      if (!normalized || normalized.rules.length === 0) {
        debugLog('no usable manifest; engine dormant');
        return;
      }
      state.manifest = normalized;
      state.active = true;
      api.active = true;
      /* Catch up: a cart that qualified before activation (the page-load cart,
       * or a mutation during manifest load) reconciles now. Review R2-2: the
       * embedded-state shortcut needed section HTML we don't have — use the
       * theme's queued authoritative read instead (deduped in-flight; one
       * init-time read, never on a shopper's add path). */
      if (state.lastCart) {
        state.passCount = 0;
        reconcile(state.lastCart, { fromEngine: false });
        return;
      }
      try {
        var cartApi = themeCart();
        if (cartApi && typeof cartApi.getCart === 'function') {
          cartApi.getCart().then(function (cart) {
            var cartState = cartApi.getCartState ? cartApi.getCartState(cart) : cart;
            if (!cartState || !Array.isArray(cartState.items)) return;
            /* A shopper event that raced the read wins. */
            if (state.lastCart) return;
            state.lastCart = cartState;
            state.passCount = 0;
            reconcile(cartState, { fromEngine: false });
          }, function () { /* fail open: the next shopper event syncs us */ });
        }
      } catch (err) { /* fail open */ }
    };

    try {
      if (manifestSource && typeof manifestSource.then === 'function') {
        manifestSource.then(activate, function (err) {
          debugLog('manifest load failed; engine dormant', err && err.message);
        });
      } else if (manifestSource) {
        activate(manifestSource);
      } else {
        debugLog('no manifest source; engine dormant');
      }
    } catch (err) {
      debugLog('init failed open', err);
    }
    return api;
  }

  var api = {
    version: ENGINE_VERSION,
    active: false,
    evaluate: evaluate,
    normalizeManifest: normalizeManifest,
    init: init,
    selectChoice: selectChoice,
    selectChoices: selectChoices,
    clearChoices: clearChoices,
    reconcile: function (cart) { return reconcile(cart, { fromEngine: false }); },
    _test: {
      state: state,
      computeDiff: computeDiff,
      detectOptOuts: detectOptOuts,
      parseRewardToken: parseRewardToken,
      decodeIdArray: decodeIdArray,
      legacyRewardLines: legacyRewardLines,
      seedLegacyChoicePicks: seedLegacyChoicePicks
    }
  };

  if (typeof window !== 'undefined') {
    window.IBGiftEngine = api;
    if (!window.IB_GIFT_ENGINE_MANUAL) {
      if (typeof document !== 'undefined' && document.readyState === 'loading' && document.addEventListener) {
        document.addEventListener('DOMContentLoaded', function () { init(); });
      } else {
        init();
      }
    }
  }
})();
